import { useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { aiApi, isAiGenerationCancelled } from '@/api/ai';
import { useModalContext } from '@/components/ui/modal-provider';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { captureFeatureBlocked } from '@/lib/posthog';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import AIGeneratorCard from './AIGeneratorCard';
import AnamnesisUploadModal from './AnamnesisUploadModal';
import { SECTIONS } from './constants';
import { WizardStepProps } from './types';
import type { SourceActionsMap } from './AIDataSources';

export default function DemandaStep({ data, onUpdate }: WizardStepProps) {
    const {
        silentSave,
        sectionRequirements,
        setIsGenerating,
        setGeneratingSectionId,
        beginGeneration,
        endGeneration,
    } = useReportWizard();
    const { openModal } = useModalContext();
    const anamnesisAccess = useFeatureEntitlement('anamnesis.process');

    // `data` and `onUpdate` are fresh on every keystroke in the document —
    // reading them through a ref keeps the action handlers (and therefore
    // `sourceActions`) stable, so the panel stops re-rendering with new props.
    const latest = useRef({ data, onUpdate });
    latest.current = { data, onUpdate };

    const handleOpenAnamnesisModal = useCallback(() => {
        if (anamnesisAccess.shouldBlock) {
            captureFeatureBlocked('anamnesis_modal', anamnesisAccess.decision);
            toast.warning(getEntitlementBlockedMessage(anamnesisAccess.decision));
            return;
        }

        openModal({
            // No title here on purpose: the modal renders its own
            // `AppDialogLayout` so the paste view can put a back control in the
            // header row, next to the title and the close.
            content: (
                <AnamnesisUploadModal
                    data={latest.current.data}
                    onUpdate={latest.current.onUpdate}
                />
            ),
            size: 'lg',
        });
    }, [anamnesisAccess.shouldBlock, anamnesisAccess.decision, openModal]);

    const sourceActions = useMemo<SourceActionsMap>(
        () => ({
            anamnesis: {
                // "Adicionar" enquanto falta, "Gerenciar" depois que entra: o
                // rótulo nomeia o que você vai fazer lá, não o componente.
                label: 'Adicionar anamnese',
                manageLabel: 'Gerenciar anamnese',
                disabled: anamnesisAccess.shouldBlock,
                disabledTooltip: getEntitlementBlockedMessage(anamnesisAccess.decision),
                unavailablePlan:
                    anamnesisAccess.isUnavailableInPlan && anamnesisAccess.availableFromPlan
                        ? anamnesisAccess.availableFromPlan
                        : null,
                onClick: handleOpenAnamnesisModal,
            },
        }),
        [
            anamnesisAccess.shouldBlock,
            anamnesisAccess.decision,
            anamnesisAccess.isUnavailableInPlan,
            anamnesisAccess.availableFromPlan,
            handleOpenAnamnesisModal,
        ],
    );

    const handleGenerate = async () => {
        if (!data.id) return;

        setIsGenerating(true);
        setGeneratingSectionId(SECTIONS.DEMAND_DESCRIPTION);
        const generation = beginGeneration();
        await silentSave();
        try {
            const response = await aiApi.generate(
                data.id,
                SECTIONS.DEMAND_DESCRIPTION,
                undefined,
                'create',
                undefined,
                generation.options,
            );
            onUpdate((prevData) => ({
                demand_description: {
                    ...prevData.demand_description,
                    content: response.content,
                },
            }));
            toast.success('Rascunho da demanda gerado. Revise no laudo ao lado.');
        } catch (error: unknown) {
            // The user stopped it. The section is untouched and no credit was
            // spent, and the button they pressed is answer enough.
            if (isAiGenerationCancelled(error)) return;

            toast.error((error as Error).message);
        } finally {
            setIsGenerating(false);
            setGeneratingSectionId(null);
            endGeneration();
        }
    };

    return (
        <div className="flex h-full flex-col">
            <AIGeneratorCard
                onGenerate={handleGenerate}
                sourceStepId={SECTIONS.DEMAND_DESCRIPTION}
                reportData={data}
                sourceActions={sourceActions}
                backendRequirements={sectionRequirements}
            />
        </div>
    );
}
